import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
  Upload, Users, BarChart3, Play, Pause, Download,
  FileText, Filter, X, CheckCircle, AlertCircle,
  Loader2, MessageSquare
} from 'lucide-react';
import '../styles/personaInterview.css'; // Reuse existing styles

function PersonaSurveyQuant() {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // Survey setup state
  const [currentView, setCurrentView] = useState('setup'); // setup, running, results
  const [csvFile, setCsvFile] = useState(null);
  const [surveyTitle, setSurveyTitle] = useState('');
  const [surveyType, setSurveyType] = useState('upload'); // 'upload' or 'existing'

  // Existing surveys state
  const [existingSurveys, setExistingSurveys] = useState([]);
  const [surveysLoading, setSurveysLoading] = useState(false);
  const [selectedExistingSurvey, setSelectedExistingSurvey] = useState(null);

  // Persona selection state (reuse from PersonaInterview)
  const [selectedPersonas, setSelectedPersonas] = useState([]);
  const [personaFilters, setPersonaFilters] = useState({
    generation_target: '',
    actual_audience: '',
    location: '',
    genre: '',
    profile_name: ''
  });
  const [availablePersonas, setAvailablePersonas] = useState([]);
  const [personasLoading, setPersonasLoading] = useState(false);

  // Survey execution state
  const [surveyRunning, setSurveyRunning] = useState(false);
  const [surveyProgress, setSurveyProgress] = useState(0);
  const [surveyResults, setSurveyResults] = useState([]);
  const [currentPersonaIndex, setCurrentPersonaIndex] = useState(0);
  const [surveyId, setSurveyId] = useState('');
  const [surveyStatus, setSurveyStatus] = useState('');
  const [pollInterval, setPollInterval] = useState(null);

  const fileInputRef = useRef(null);

  // Fetch personas (reuse logic from PersonaInterview)
  useEffect(() => {
    const fetchPersonas = async () => {
      try {
        setPersonasLoading(true);

        const queryParams = new URLSearchParams();
        if (personaFilters.generation_target) queryParams.append('generation_target', personaFilters.generation_target);
        if (personaFilters.actual_audience) queryParams.append('actual_audience', personaFilters.actual_audience);
        if (personaFilters.location) queryParams.append('location', personaFilters.location);
        if (personaFilters.genre) queryParams.append('genre', personaFilters.genre);
        if (personaFilters.profile_name) queryParams.append('profile_name', personaFilters.profile_name);
        queryParams.append('limit', '50');

        const response = await fetch(`https://n8n.iniai.cloud/webhook/get-synthetic-fans?${queryParams.toString()}`, {
          method: 'GET',
          headers: {
            'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();
        const transformedPersonas = data.map(persona => ({
          id: persona.id || persona.foundation_id, // Use numeric ID if available, fallback to foundation_id
          foundation_id: persona.foundation_id, // Keep foundation_id as separate field
          name: persona.profile_name.replace(/_/g, ' '),
          title: extractTitle(persona.demographic_summary),
          industry: extractIndustry(persona.foundation_id, persona.cultural_context),
          department: extractDepartment(persona.demographic_summary, persona.cultural_context),
          experience: extractExperience(persona.demographic_summary),
          company: extractCompany(persona.cultural_context, persona.demographic_summary),
          description: persona.demographic_summary || 'Music fan profile',
          traits: extractTraits(persona.demographic_summary, persona.cultural_context),
          original: {
            id: persona.id || persona.foundation_id,
            foundation_id: persona.foundation_id,
            profile_name: persona.profile_name,
            demographic_summary: persona.demographic_summary,
            cultural_context: persona.cultural_context,
            profile_data: persona.profile_data,
            generation_target: persona.generation_target
          }
        }));

        setAvailablePersonas(transformedPersonas);
      } catch (error) {
        console.error('Error fetching personas:', error);
      } finally {
        setPersonasLoading(false);
      }
    };

    fetchPersonas();
  }, [personaFilters]);

  // Fetch existing surveys
  useEffect(() => {
    const fetchExistingSurveys = async () => {
      try {
        setSurveysLoading(true);

        const response = await fetch('https://n8n.iniai.cloud/webhook/get-existing-surveys', {
          method: 'GET',
          headers: {
            'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
            'Content-Type': 'application/json'
          },
          credentials: 'include'
        });

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const data = await response.json();
        setExistingSurveys(data);
      } catch (error) {
        console.error('Error fetching existing surveys:', error);
      } finally {
        setSurveysLoading(false);
      }
    };

    fetchExistingSurveys();
  }, []); // Run once on component mount

  // Helper functions (simplified from PersonaInterview)
  const extractTitle = (demographic) => {
    if (!demographic) return 'Fan';
    const parts = demographic.split(',').map(p => p.trim());
    if (parts.length >= 2) {
      const potentialTitle = parts[1];
      if (potentialTitle && !potentialTitle.match(/^\$|\d+K|years?\s+clean|male|female/i)) {
        return potentialTitle;
      }
    }
    return 'Fan';
  };

  const extractIndustry = (foundationId, cultural) => {
    // Extract artist from foundation_id pattern (e.g., "jelly_roll_20250923_1135")
    if (foundationId && foundationId.includes('_')) {
      const artistPart = foundationId.split('_').slice(0, -2).join(' '); // Remove date/time parts
      const artistName = artistPart.replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      return `${artistName} Audience`;
    }

    // Fallback to extracting from cultural context for actual industry
    if (cultural) {
      const text = cultural.toLowerCase();
      if (text.includes('tech') || text.includes('software')) return 'Technology';
      if (text.includes('healthcare') || text.includes('medical')) return 'Healthcare';
      if (text.includes('education') || text.includes('school')) return 'Education';
      if (text.includes('finance') || text.includes('bank')) return 'Finance';
    }

    return 'Music Audience';
  };

  const extractDepartment = (demographic, cultural) => {
    if (!demographic && !cultural) return 'Fanbase';

    const text = `${demographic || ''} ${cultural || ''}`.toLowerCase();

    // Extract actual job/role context
    if (text.includes('engineer') || text.includes('developer')) return 'Engineering';
    if (text.includes('doctor') || text.includes('nurse')) return 'Medical';
    if (text.includes('teacher') || text.includes('professor')) return 'Education';
    if (text.includes('manager') || text.includes('director')) return 'Management';
    if (text.includes('student')) return 'Student Life';
    if (text.includes('union') || text.includes('trade')) return 'Labor';

    return 'Community';
  };

  const extractExperience = (demographic) => {
    if (!demographic) return 'Music Fan';

    const text = demographic.toLowerCase();
    if (text.includes('senior') || text.includes('veteran') || text.includes('experienced')) return 'Experienced';
    if (text.includes('junior') || text.includes('entry') || text.includes('new')) return 'Entry Level';
    if (text.includes('student') || text.includes('recent')) return 'Student/Recent';

    return 'Professional';
  };

  const extractCompany = (cultural, demographic) => {
    if (!cultural && !demographic) return 'Independent';

    const text = `${cultural || ''} ${demographic || ''}`;

    // Look for specific company/workplace mentions first
    const workMatch = text.match(/(?:works? at|company|employed by)[:\s]+([^,.\n]+)/i);
    if (workMatch) return workMatch[1].trim();

    // Extract location from cultural_context - handle various formats
    if (cultural) {
      // Clean up the cultural context and extract main location
      let location = cultural
        .replace(/\s+Context$/, '') // Remove "Context" suffix
        .replace(/Music Scene|Tech Industry|Indie Scene|Urban Economic Pressure|Class Dynamics/, '') // Remove descriptors
        .replace(/\s+$/, '') // Trim trailing spaces
        .split('/')[0] // Take first part if slash-separated
        .split(',')[0]; // Take first part if comma-separated

      // Handle specific location formats
      if (location.includes('Appalachian')) return location;
      if (location.includes('Eastern Kentucky')) return 'Eastern Kentucky';
      if (location.includes('Kentucky') && location.includes('Nashville')) return 'Kentucky/Nashville';
      if (location.includes('Brooklyn')) return 'Brooklyn, NY';
      if (location.includes('Capitol Hill')) return 'Seattle, WA';
      if (location.includes('Bay Area') || location.includes('Silicon Valley')) return 'San Francisco Bay Area';
      if (location.includes('Rust Belt')) return 'Pittsburgh, PA';

      // Standard city extraction
      if (location.length > 2) return location;
    }

    return 'Various';
  };

  const extractTraits = (demographic, cultural) => {
    const traits = [];
    const text = `${demographic || ''} ${cultural || ''}`.toLowerCase();

    // Extract gender traits (order matters - check female first!)
    if (text.includes('non-binary') || text.includes('nonbinary') || text.includes('non binary')) {
      traits.push('Non-binary');
    } else if (text.includes('female')) {
      traits.push('Female');
    } else if (text.includes(' male') || text.match(/\bmale\b/)) {
      traits.push('Male');
    }

    // Extract specific life circumstances (higher priority)
    if (text.includes('recovering') || text.includes('clean') || text.includes('sober')) traits.push('In Recovery');
    if (text.includes('single parent') || text.includes('single mom') || text.includes('single dad')) traits.push('Single Parent');
    else if (text.includes('parent')) traits.push('Parent');

    // Extract work/class status (prioritize more specific)
    if (text.includes('union') && text.includes('professional')) {
      traits.push('Union Professional'); // More specific than either alone
    } else if (text.includes('union') || (text.includes('worker') && !text.includes('professional'))) {
      traits.push('Working Class');
    } else if (text.includes('professional')) {
      traits.push('Professional');
    }

    // Extract education
    if (text.includes('college') || text.includes('university')) traits.push('College Educated');
    if (text.includes('student')) traits.push('Student');

    // Extract community connection
    if (text.includes('local') || text.includes('community')) traits.push('Community Oriented');

    return traits.length > 0 ? traits.slice(0, 3) : ['Music Fan']; // Limit to 3 traits
  };

  // CSV file handling
  const handleFileUpload = (event) => {
    console.log('handleFileUpload called', event.target.files);
    const file = event.target.files[0];
    console.log('Selected file:', file);
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      console.log('Setting CSV file:', file.name);
      setCsvFile(file);
      setSurveyTitle(file.name.replace('.csv', ''));
    } else if (file) {
      console.log('File rejected - type:', file.type, 'name:', file.name);
      alert('Please select a CSV file');
    }
  };


  // Survey execution
  const startSurvey = async () => {
    if (((surveyType === 'upload' && !csvFile) || (surveyType === 'existing' && !selectedExistingSurvey)) || selectedPersonas.length === 0) return;

    setSurveyRunning(true);
    setCurrentView('running');
    setSurveyProgress(0);
    setSurveyResults([]);
    setCurrentPersonaIndex(0);

    try {
      const surveyData = {
        survey_type: surveyType,
        survey_id: selectedExistingSurvey ? selectedExistingSurvey.template_id : null,
        personas: selectedPersonas.map(p => ({
          id: p.id,
          foundation_id: p.original.foundation_id
        })),
        survey_title: surveyTitle || 'Quantitative Survey',
        user: {
          id: user?.id || '',
          email: user?.email || '',
          name: user?.name || ''
        }
      };

      let requestBody;
      let headers = {
        'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
      };

      // Both upload and existing surveys use FormData format
      const formData = new FormData();

      // Add CSV file only for upload type
      if (surveyType === 'upload' && csvFile) {
        formData.append('csv_file', csvFile);
      }

      // Add survey data fields (same for both types)
      formData.append('survey_type', surveyData.survey_type);
      formData.append('survey_title', surveyData.survey_title);
      formData.append('user_id', surveyData.user.id);
      formData.append('user_email', surveyData.user.email);
      formData.append('user_name', surveyData.user.name);

      // Add survey_id for existing surveys
      if (surveyType === 'existing' && surveyData.survey_id) {
        formData.append('survey_id', surveyData.survey_id);
      }

      // Send personas as comma-separated lists
      const personaIds = surveyData.personas.map(persona => persona.id).join(',');
      const foundationIds = surveyData.personas.map(persona => persona.foundation_id).join(',');
      formData.append('persona_ids', personaIds);
      formData.append('foundation_ids', foundationIds);
      formData.append('persona_count', surveyData.personas.length);

      requestBody = formData;

      const response = await fetch('https://n8n.iniai.cloud/webhook/synth_survey_select', {
        method: 'POST',
        headers: headers,
        credentials: 'include',
        body: requestBody
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const data = await response.json();

      // Display the response
      setSurveyId(data.survey_id);
      setSurveyStatus(data.message);

      // Start simple polling
      pollSurveyStatus(data.survey_id, data.personas_selected);
    } catch (error) {
      console.error('Survey error:', error);
      alert('Survey failed. Please try again.');
      setSurveyRunning(false);
    }
  };

  const pollSurveyStatus = async (surveyId, expectedResponses) => {
    const maxAttempts = 60; // 10 minutes (60 × 10 seconds)
    let attempts = 0;

    const poll = setInterval(async () => {
      attempts++;

      try {
        const response = await fetch(
          `https://n8n.iniai.cloud/webhook/survey_status?survey_id=${surveyId}&survey_name=${encodeURIComponent(surveyTitle)}&expected_responses=${expectedResponses}`,
          {
            method: 'GET',
            headers: {
              'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
              // 'Content-Type': 'application/json'
            },
            credentials: 'include'
          }
        );
        const rawData = await response.json();

        // Handle both array and object responses
        const data = Array.isArray(rawData) ? rawData[0] : rawData;

        // Debug: Log the entire response
        console.log('Polling response (raw):', rawData);
        console.log('Polling response (parsed):', data);
        console.log('Progress:', data.progress);
        console.log('Status:', data.status);
        console.log('Message:', data.message);
        console.log('Should continue polling:', data.should_continue_polling);

        // Update UI with progress
        setSurveyProgress(data.progress || 0);
        setSurveyStatus(data.message || 'Processing...');

        if (data.status === "completed" || !data.should_continue_polling || attempts >= maxAttempts) {
          clearInterval(poll);
          setPollInterval(null);

          if (data.status === "completed" || !data.should_continue_polling) {
            // Fetch full results
            loadSurveyResults(surveyId);
          } else {
            // Timeout
            setSurveyStatus('Survey timed out. Please check results manually.');
            setSurveyRunning(false);
          }
        }
      } catch (error) {
        console.error('Polling error:', error);
        if (attempts >= maxAttempts) {
          clearInterval(poll);
          setPollInterval(null);
          setSurveyStatus('Error checking survey status.');
          setSurveyRunning(false);
        }
      }
    }, 10000); // Poll every 10 seconds

    setPollInterval(poll);
  };

  const loadSurveyResults = async (surveyId) => {
    try {
      const response = await fetch(
        `https://n8n.iniai.cloud/webhook/survey_results?survey_id=${surveyId}`,
        {
          headers: {
            'x-api-key': process.env.REACT_APP_CREATE_USER_API_KEY,
          },
          credentials: 'include'
        }
      );
      const results = await response.json();
      setSurveyResults(results);
      setSurveyProgress(100);
      setCurrentView('results');
      setSurveyRunning(false);
    } catch (error) {
      console.error('Error loading results:', error);
      setSurveyStatus('Error loading survey results.');
      setSurveyRunning(false);
    }
  };

  const togglePersonaSelection = (persona) => {
    setSelectedPersonas(prev => {
      const isSelected = prev.find(p => p.id === persona.id);
      if (isSelected) {
        return prev.filter(p => p.id !== persona.id);
      } else {
        return [...prev, persona];
      }
    });
  };

  const resetFilters = () => {
    setPersonaFilters({
      generation_target: '',
      actual_audience: '',
      location: '',
      genre: '',
      profile_name: ''
    });
  };

  const resetSurvey = () => {
    setCurrentView('setup');
    setSurveyResults([]);
    setSurveyProgress(0);
    setSurveyRunning(false);
  };

  const exportResultsToCSV = () => {
    if (!surveyResults || surveyResults.length === 0) return;

    const headers = ['Persona Name', 'Persona ID', 'Foundation ID', 'Question', 'Answer'];
    const rows = [headers];

    surveyResults.forEach(result => {
      result.responses?.forEach(response => {
        rows.push([
          result.persona_name || '',
          result.persona_id || '',
          result.persona_foundation_id || '',
          response.question || '',
          response.answer || ''
        ]);
      });
    });

    const csvContent = rows.map(row =>
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${surveyTitle || 'survey_results'}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };


  return (
    <div className="persona-interview-container">
      {currentView === 'setup' && (
        <div className="persona-selection">
          {/* Header */}
          <div className="page-header">
            <div className="header-content">
              <div className="header-title">
                <BarChart3 size={24} />
                <h1>Quantitative Persona Survey</h1>
              </div>
              <p>Run structured surveys across multiple personas</p>
            </div>
          </div>

          {/* Survey Setup */}
          <div className="filters-section">
            <div className="filters-header">
              <FileText size={20} />
              <h3>Survey Setup</h3>
            </div>

            <div className="survey-setup-grid">
              <div className="setup-group full-width">
                <label>Survey Source</label>
                <div className="survey-type-toggle">
                  <button
                    type="button"
                    className={`survey-type-btn ${surveyType === 'upload' ? 'active' : ''}`}
                    onClick={() => {
                      console.log('Upload CSV clicked', fileInputRef.current);
                      setSurveyType('upload');
                      setSelectedExistingSurvey(null);
                      setSurveyTitle('');
                      // Directly trigger file picker
                      if (fileInputRef.current) {
                        fileInputRef.current.click();
                      }
                    }}
                  >
                    <Upload size={16} />
                    Upload New CSV
                  </button>
                  <button
                    type="button"
                    className={`survey-type-btn ${surveyType === 'existing' ? 'active' : ''}`}
                    onClick={() => {
                      setSurveyType('existing');
                      setCsvFile(null);
                      setSurveyTitle('');
                    }}
                  >
                    <FileText size={16} />
                    Use Existing Survey
                  </button>
                </div>

                {/* Hidden file input - always present */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                />

                {surveyType === 'upload' && (
                  <div className="csv-upload-section">

                    {!csvFile ? (
                      <div className="upload-area" style={{ cursor: 'pointer' }}>
                        <Upload size={48} />
                        <h3>Upload CSV Survey</h3>
                        <p>Click to select a CSV file with your survey questions</p>
                        <button
                          type="button"
                          className="upload-btn"
                          onClick={(e) => {
                            e.preventDefault();
                            console.log('Upload button clicked', fileInputRef.current);
                            fileInputRef.current?.click();
                          }}
                        >
                          Choose File
                        </button>
                      </div>
                    ) : (
                      <div className="csv-file-info">
                        <div className="csv-file-details">
                          <FileText size={20} />
                          <div>
                            <h4>{csvFile.name}</h4>
                            <p>CSV file ready for upload</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setCsvFile(null)}
                          className="remove-csv-btn"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    )}

                    {csvFile && (
                      <div className="setup-group">
                        <label>Survey Title</label>
                        <input
                          type="text"
                          value={surveyTitle}
                          onChange={(e) => setSurveyTitle(e.target.value)}
                          placeholder="Enter title for this survey"
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Existing Surveys Section - only show when 'existing' is selected */}
          {surveyType === 'existing' && (
            <div className="filters-section">
              <div className="filters-header">
                <FileText size={20} />
                <h3>Select Existing Survey</h3>
              </div>

              {surveysLoading && (
                <div className="surveys-loading">
                  <div className="loading-spinner"></div>
                  <p>Loading surveys...</p>
                </div>
              )}

              {!surveysLoading && existingSurveys.length > 0 && (
                <div className="existing-surveys-grid">
                  {existingSurveys.map(survey => (
                    <div
                      key={survey.template_id}
                      className={`survey-card ${selectedExistingSurvey?.template_id === survey.template_id ? 'selected' : ''}`}
                    >
                      <div className="survey-header">
                        <h4>{survey.template_name}</h4>
                        <span className="survey-status">{survey.status}</span>
                      </div>

                      {survey.template_description && (
                        <p className="survey-description">{survey.template_description}</p>
                      )}

                      <div className="survey-meta">
                        <span className="survey-meta-item">
                          {Array.isArray(survey.questions_data) ? survey.questions_data.length : 0} questions
                        </span>
                        <span className="survey-meta-item">
                          Created: {new Date(survey.created_at).toLocaleDateString()}
                        </span>
                      </div>

                      <div className="survey-questions-preview">
                        <h5>Questions Preview:</h5>
                        {Array.isArray(survey.questions_data) && survey.questions_data.slice(0, 2).map((q, index) => (
                          <div key={index} className="question-preview">
                            <span className="question-number">Q{q.display_order}</span>
                            <span className="question-text">{q.question_text}</span>
                          </div>
                        ))}
                        {Array.isArray(survey.questions_data) && survey.questions_data.length > 2 && (
                          <p className="more-questions">+{survey.questions_data.length - 2} more questions</p>
                        )}
                      </div>

                      <button
                        onClick={() => {
                          setSelectedExistingSurvey(survey);
                          setSurveyTitle(survey.template_name);
                        }}
                        className={`load-survey-btn ${selectedExistingSurvey?.template_id === survey.template_id ? 'selected' : ''}`}
                      >
                        {selectedExistingSurvey?.template_id === survey.template_id ? 'Selected' : 'Select Survey'}
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {!surveysLoading && existingSurveys.length === 0 && (
                <div className="no-surveys">
                  <FileText size={48} />
                  <h3>No existing surveys found</h3>
                  <p>Upload a CSV to create your first survey</p>
                </div>
              )}
            </div>
          )}

          {/* Persona Filters - Reuse from PersonaInterview */}
          <div className="filters-section">
            <div className="filters-header">
              <Filter size={20} />
              <h3>Filter Personas</h3>
              <div className="filter-actions">
                <button onClick={resetFilters} className="reset-filters-btn">
                  Reset Filters
                </button>

                {/* Run Survey Button */}
                {((surveyType === 'upload' && csvFile) || (surveyType === 'existing' && selectedExistingSurvey)) && (
                  <button
                    onClick={() => {
                      setSelectedPersonas(availablePersonas);
                      startSurvey();
                    }}
                    className="run-survey-btn"
                    disabled={availablePersonas.length === 0}
                  >
                    <Play size={16} />
                    Run Survey ({selectedPersonas.length > 0 ? selectedPersonas.length : availablePersonas.length} personas)
                  </button>
                )}
              </div>
            </div>

            <div className="filters-grid">
              <div className="filter-group">
                <label>Seed Artist/Genre</label>
                <input
                  type="text"
                  value={personaFilters.generation_target}
                  onChange={(e) => setPersonaFilters(prev => ({ ...prev, generation_target: e.target.value }))}
                  placeholder="e.g. Jelly Roll, Taylor Swift"
                />
              </div>
              <div className="filter-group">
                <label>Actual Audience</label>
                <input
                  type="text"
                  value={personaFilters.actual_audience}
                  onChange={(e) => setPersonaFilters(prev => ({ ...prev, actual_audience: e.target.value }))}
                  placeholder="e.g. working class, blue collar"
                />
              </div>
              <div className="filter-group">
                <label>Location</label>
                <input
                  type="text"
                  value={personaFilters.location}
                  onChange={(e) => setPersonaFilters(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="e.g. Kentucky, Nashville"
                />
              </div>
              <div className="filter-group">
                <label>Genre</label>
                <input
                  type="text"
                  value={personaFilters.genre}
                  onChange={(e) => setPersonaFilters(prev => ({ ...prev, genre: e.target.value }))}
                  placeholder="e.g. country, rock, pop"
                />
              </div>
            </div>
          </div>

          {/* Persona Selection */}
          <div className="filters-section">
            <div className="filters-header">
              <Users size={20} />
              <h3>Select Personas ({selectedPersonas.length} selected)</h3>
              {selectedPersonas.length > 0 && (
                <button
                  onClick={() => setSelectedPersonas([])}
                  className="reset-filters-btn"
                >
                  Clear Selection
                </button>
              )}
            </div>

            {personasLoading && (
              <div className="personas-loading">
                <div className="loading-spinner"></div>
                <p>Loading personas...</p>
              </div>
            )}

            {!personasLoading && (
              <div className="personas-grid">
                {availablePersonas.map(persona => (
                  <div
                    key={persona.id}
                    className={`persona-card ${selectedPersonas.find(p => p.id === persona.id) ? 'selected' : ''}`}
                    onClick={() => togglePersonaSelection(persona)}
                  >
                    <div className="persona-header">
                      <div className="persona-avatar">
                        <Users size={32} />
                      </div>
                      <div className="persona-info">
                        <h3>{persona.name}</h3>
                        <p className="persona-title">{persona.title}</p>
                        <p className="persona-company">{persona.company}</p>
                      </div>
                      {selectedPersonas.find(p => p.id === persona.id) && (
                        <CheckCircle className="selection-indicator" size={20} />
                      )}
                    </div>
                    <div className="persona-details">
                      <p className="persona-description">{persona.description}</p>

                      <div className="persona-meta">
                        <span className="meta-badge industry">{persona.industry}</span>
                        <span className="meta-badge department">{persona.department}</span>
                        <span className="meta-badge experience">{persona.experience}</span>
                      </div>

                      <div className="persona-traits">
                        {persona.traits.map(trait => (
                          <span key={trait} className="trait-tag">{trait}</span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

          </div>
        </div>
      )}

      {currentView === 'running' && (
        <div className="survey-running">
          <div className="survey-header">
            <div className="survey-info">
              <BarChart3 size={24} />
              <div>
                <h2>{surveyTitle || 'Quantitative Survey'}</h2>
                <p>Running survey across {selectedPersonas.length} personas</p>
                {surveyId && <p className="survey-id">Survey ID: {surveyId}</p>}
              </div>
            </div>
            <button onClick={resetSurvey} className="back-btn">
              ← Back to Setup
            </button>
          </div>

          <div className="survey-progress">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${surveyProgress}%` }}
              ></div>
            </div>
            <p>{surveyProgress}% Complete</p>

            {surveyRunning && (
              <div className="survey-status">
                <Loader2 className="spinner" size={20} />
                <span>{surveyStatus || 'Processing responses...'}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {currentView === 'results' && (
        <div className="survey-results">
          <div className="results-header">
            <div className="results-info">
              <BarChart3 size={24} />
              <div>
                <h2>{surveyTitle || 'Survey Results'}</h2>
                <p>{surveyResults.length} responses collected</p>
              </div>
            </div>
            <div className="results-actions">
              <button onClick={resetSurvey} className="back-btn">
                ← New Survey
              </button>
              <button onClick={exportResultsToCSV} className="download-btn">
                <Download size={16} />
                Export Results
              </button>
            </div>
          </div>

          <div className="results-content">
            {surveyResults.map((result, index) => (
              <div key={index} className="result-card">
                <div className="result-header">
                  <h3>{result.persona_name}</h3>
                  <span className="result-status">
                    <CheckCircle size={16} />
                    Complete
                  </span>
                </div>
                <div className="result-responses">
                  {result.responses?.map((response, responseIndex) => (
                    <div key={responseIndex} className="response-item">
                      <h4>Q{responseIndex + 1}: {response.question}</h4>
                      <p><strong>Answer:</strong> {response.answer}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default PersonaSurveyQuant;